import { Book, ContactDataSourceSchema } from '@eigtech/contacts-types'
import { JobId } from '@eigtech/restoration-types'
import { Corn, getCornKernels } from '@eigtech/shared-corn'
import { useGetAssignmentsForAssignable } from '@eigtech/ui-shared-assignments'
import { ContextualCan } from '@eigtech/ui-shared-auth'
import {
  isClaimBook,
  useAssignContactToClaim,
  useUnassignContactFromClaim,
} from '@eigtech/ui-shared-claims-lib'
import {
  AugmentedContact,
  EntityBookTypes,
  getBookEntityCorn,
  getContactOfTypeFromEntity,
  getContactRoleLabel,
  isEntityBook,
  useActivateContact,
  useDeactivateContact,
  useDeleteContact,
} from '@eigtech/ui-shared-contacts'
import {
  ConfirmModal,
  DangerMenuItem,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Tooltip,
  chakra,
  useCopyToClipboard,
  useDisclosure,
  useToast,
} from '@eigtech/ui-shared-dave'
import { useAssignContactToJob } from '@eigtech/ui-shared-jobs'
import { exhaustive } from 'exhaustive'
import { JSX, useState } from 'react'
import { MdMoreHoriz } from 'react-icons/md'
import { ViewContactModal } from './ContactDetail'
import { ContactFormDrawer } from './Forms/ContactForm/ContactFormDrawer'

export type ContactActionMenuProps = {
  book: Book | undefined
  contact: AugmentedContact
  menuItems?: (contact: AugmentedContact) => JSX.Element
}

export function ContactActionMenu({ book, contact, menuItems }: ContactActionMenuProps) {
  const { copyToClipboard } = useCopyToClipboard()

  const toast = useToast()

  const confirmSetPolicyholderModal = useDisclosure()
  const confirmSetPrimaryContactModal = useDisclosure()
  const confirmSetLegalRepContactModal = useDisclosure()
  const confirmRemoveLegalRepContactModal = useDisclosure()
  const confirmDeleteContactModal = useDisclosure()

  const {
    isOpen: isOpenContactModal,
    onOpen: onOpenContactModal,
    onClose: onCloseContactModal,
  } = useDisclosure()

  const [isContactFormDrawerOpen, setIsContactFormDrawerOpen] = useState(false)

  const { mutateAsync: deleteContact, isPending: isDeletingContact } = useDeleteContact()
  const { mutateAsync: activateContact, isPending: isActivatingContact } = useActivateContact()
  const { mutateAsync: deactivateContact, isPending: isDeactivatingContact } =
    useDeactivateContact()

  async function handleDeleteContact() {
    try {
      await deleteContact({ contactId: contact.contactId })
      toast({
        status: 'success',
        title: 'Successfully deleted contact',
      })
    } catch (e) {
      toast({
        status: 'error',
        title: 'Failed to delete contact',
      })
      throw e
    }
  }

  async function handleContactInactiveStatus(inactive: boolean) {
    const status = inactive ? 'inactive' : 'active'

    try {
      if (inactive) {
        await deactivateContact({ contactId: contact.contactId })
      } else {
        await activateContact({ contactId: contact.contactId })
      }

      toast({
        status: 'success',
        title: `Contact is now ${inactive ? 'inactive' : 'active'}`,
      })
    } catch (e) {
      toast({
        status: 'error',
        title: `Failed to set contact as ${status}`,
      })
      throw e
    }
  }
  const entityCorn = book ? getBookEntityCorn(book, { shouldValidate: false }) : undefined

  const { data: assignments } = useGetAssignmentsForAssignable(entityCorn ?? ('' as Corn))

  const primaryContact = getContactOfTypeFromEntity({
    assignments,
    contactType: 'primaryContact',
  })?.at(0)
  const policyholder = getContactOfTypeFromEntity({ assignments, contactType: 'policyHolder' })?.at(
    0
  )
  const legalReps = getContactOfTypeFromEntity({ assignments, contactType: 'legalRep' })

  const isPrimaryContact = primaryContact?.contactId === contact.contactId
  const isPolicyholder = policyholder?.contactId === contact.contactId
  const isLegalRep = legalReps?.some((rep) => rep.contactId === contact.contactId)

  const { mutateAsync: assignContactToClaim, isPending: isAssigningContactToClaim } =
    useAssignContactToClaim()
  const { mutateAsync: assignContactToJob, isPending: isAssigningContactToJob } =
    useAssignContactToJob()
  const { mutateAsync: unassignContactFromClaim, isPending: isUnassigningContactFromClaim } =
    useUnassignContactFromClaim()

  async function handleAssignContactToClaim(
    assigneeRelationship: 'primaryContact' | 'policyHolder' | 'legalRep'
  ) {
    if (!book || !isEntityBook(book)) return

    const bookType = book.metadata.type as EntityBookTypes
    const { resourceId } = getCornKernels(book.bookId)

    if (!resourceId) return

    const sharedProps = {
      contact,
    } as const

    try {
      await exhaustive(bookType, {
        claim: () =>
          assignContactToClaim({
            ...sharedProps,
            assigneeRelationship,
            claimNumber: resourceId,
          }),
        job: () =>
          assignContactToJob({
            ...sharedProps,
            assigneeRelationship: 'primaryContact',
            jobId: resourceId as JobId,
          }),
      })

      toast({
        status: 'success',
        title: `Successfully set ${getContactRoleLabel(assigneeRelationship)}!`,
      })
    } catch (e) {
      toast({
        status: 'error',
        title: `Failed to set ${getContactRoleLabel(assigneeRelationship)}`,
      })
      throw e
    }
  }

  async function handleUnassignContactFromClaim(assigneeRelationship: 'legalRep') {
    if (!book || !isEntityBook(book)) return

    const bookType = book.metadata.type as EntityBookTypes
    const { resourceId } = getCornKernels(book.bookId)

    if (!resourceId) return

    try {
      await exhaustive(bookType, {
        claim: () =>
          unassignContactFromClaim({
            assigneeRelationship,
            claimNumber: resourceId,
            contactId: contact.contactId,
          }),
        job: () => {
          /* not implemented */
        },
      })

      toast({
        status: 'success',
        title: `Successfully removed ${getContactRoleLabel(assigneeRelationship)}!`,
      })
    } catch (e) {
      toast({
        status: 'error',
        title: `Failed to removed ${getContactRoleLabel(assigneeRelationship)}`,
      })
      throw e
    }
  }

  const isPending =
    isDeletingContact ||
    isAssigningContactToClaim ||
    isAssigningContactToJob ||
    isActivatingContact ||
    isDeactivatingContact ||
    isUnassigningContactFromClaim

  return (
    <>
      <Menu computePositionOnMount>
        <MenuButton
          aria-label="Open contact menu"
          as={IconButton}
          icon={<Icon as={MdMoreHoriz} />}
          isLoading={isPending}
          size="sm"
        />
        <Portal>
          <MenuList>
            <>
              <MenuItem onClick={() => copyToClipboard(contact.contactId)}>
                Copy Contact ID
              </MenuItem>
              {!!contact.metadata.xactnetId && (
                <MenuItem onClick={() => copyToClipboard(String(contact.metadata.xactnetId))}>
                  Copy XactNet ID
                </MenuItem>
              )}

              <MenuItem onClick={onOpenContactModal}>View More Info</MenuItem>

              {!(contact.metadata.removed || contact.metadata.removedOn) && (
                <>
                  <ContextualCan I="update" a="claim">
                    {!!book && isEntityBook(book) && !isPrimaryContact && (
                      <MenuItem
                        isDisabled={isAssigningContactToClaim}
                        onClick={confirmSetPrimaryContactModal.onOpen}
                      >
                        Set as Primary Contact
                      </MenuItem>
                    )}

                    {/* Only allow setting policyholder if it's a claim book and contact is not policyholder */}
                    {!!book && isClaimBook(book) && !isPolicyholder && (
                      <MenuItem
                        isDisabled={isAssigningContactToClaim}
                        onClick={confirmSetPolicyholderModal.onOpen}
                      >
                        Set as Policyholder
                      </MenuItem>
                    )}
                    {!!book && isClaimBook(book) && !isLegalRep && (
                      <MenuItem
                        isDisabled={isAssigningContactToClaim}
                        onClick={confirmSetLegalRepContactModal.onOpen}
                      >
                        Set as Legal Rep
                      </MenuItem>
                    )}
                    {!!book && isClaimBook(book) && isLegalRep && (
                      <MenuItem
                        isDisabled={isUnassigningContactFromClaim}
                        onClick={confirmRemoveLegalRepContactModal.onOpen}
                      >
                        Remove as Legal Rep
                      </MenuItem>
                    )}
                  </ContextualCan>

                  <ContextualCan I="update" a="contact">
                    <MenuItem
                      isDisabled={isPending}
                      onClick={() => setIsContactFormDrawerOpen(true)}
                    >
                      Edit Contact
                    </MenuItem>
                  </ContextualCan>

                  <ContextualCan I="update" a="contact">
                    <MenuItem
                      isDisabled={isPending}
                      onClick={() => handleContactInactiveStatus(!contact.metadata.inactive)}
                    >
                      Set as {contact.metadata.inactive ? 'Active' : 'Inactive'}
                    </MenuItem>
                  </ContextualCan>

                  {contactIsDeletable(contact) && (
                    <ContextualCan I="delete" a="contact">
                      <Tooltip
                        isDisabled={!(isPrimaryContact || isPolicyholder)}
                        label="Deleting primary contact or policyholder is not allowed"
                        placement="top"
                      >
                        <chakra.span w="full">
                          <DangerMenuItem
                            isDisabled={isPrimaryContact || isPolicyholder}
                            onClick={confirmDeleteContactModal.onOpen}
                          >
                            Delete Contact
                          </DangerMenuItem>
                        </chakra.span>
                      </Tooltip>
                    </ContextualCan>
                  )}
                </>
              )}

              {menuItems?.(contact)}
            </>
          </MenuList>
        </Portal>
      </Menu>

      {isContactFormDrawerOpen && (
        <ContactFormDrawer
          bookId={book?.bookId}
          contact={contact}
          open={isContactFormDrawerOpen}
          onOpenChange={setIsContactFormDrawerOpen}
        />
      )}

      {isOpenContactModal && (
        <ViewContactModal
          contact={contact}
          isOpen={isOpenContactModal}
          onClose={onCloseContactModal}
        />
      )}

      {!!book && isClaimBook(book) && confirmSetPolicyholderModal.isOpen && (
        <ConfirmModal
          {...confirmSetPolicyholderModal}
          isPending={isPending}
          title="Set Policyholder"
          onConfirm={() => handleAssignContactToClaim('policyHolder')}
        >
          Are you sure you want to set this contact as the policyholder?
        </ConfirmModal>
      )}

      {!!book && isEntityBook(book) && confirmSetPrimaryContactModal.isOpen && (
        <ConfirmModal
          {...confirmSetPrimaryContactModal}
          isPending={isPending}
          title="Set Primary Contact"
          onConfirm={() => handleAssignContactToClaim('primaryContact')}
        >
          Are you sure you want to set this contact as the primary contact?
        </ConfirmModal>
      )}

      {!!book && isEntityBook(book) && confirmSetLegalRepContactModal.isOpen && (
        <ConfirmModal
          {...confirmSetLegalRepContactModal}
          isPending={isPending}
          title="Set Legal Rep"
          onConfirm={() => handleAssignContactToClaim('legalRep')}
        >
          Are you sure you want to set this contact as the legal representation?
        </ConfirmModal>
      )}

      {!!book && isEntityBook(book) && confirmRemoveLegalRepContactModal.isOpen && (
        <ConfirmModal
          {...confirmRemoveLegalRepContactModal}
          isPending={isPending}
          title="Remove Legal Rep"
          onConfirm={() => handleUnassignContactFromClaim('legalRep')}
        >
          Are you sure you want to remove this contact as the legal representation?
        </ConfirmModal>
      )}

      {contactIsDeletable(contact) && confirmDeleteContactModal.isOpen && (
        <ConfirmModal
          {...confirmDeleteContactModal}
          confirmLabel="Delete"
          confirmProps={{ colorScheme: 'red' }}
          isPending={isDeletingContact}
          title="Delete Contact"
          onConfirm={handleDeleteContact}
        >
          Are you sure you want to delete this contact?
        </ConfirmModal>
      )}
    </>
  )
}

const contactIsDeletable = (contact: AugmentedContact) =>
  ([ContactDataSourceSchema.enum.CSR, ContactDataSourceSchema.enum.Estimator] as string[]).includes(
    contact.metadata.dataSource
  )
